import type { ReactElement } from "react";
import { useAppContext } from "~/contexts/appContext";
import { hasBillingGPCheckerRole } from "~/utils/userRoles";
import { CheckerFooter } from "./CheckerFooter";
import { GpCheckerFooter } from "./GpCheckerFooter";
import { MakerFooter } from "./MakerFooter";
import * as styles from "./styles_vanilla.css";
import { isGpEumRequestType } from "./utils/isGpEumRequestType";
import type { EumApproverPickerSubmitArgs } from "./EumApproverPickerModal/types";
import type { ReviewerConclusion } from "./types";
import type { BillingCaseRowItem, CaseStatus } from "../types";

export type CaseDetailsFooterProps = {
  billingCase: BillingCaseRowItem;
  onConclusionSubmit: (conclusion: ReviewerConclusion) => void;
  onStatusChange: (status: CaseStatus) => void;
  isChecker: boolean;
  isDraft?: boolean;
  hasLocalChanges?: boolean;
  hasPendingEumChanges?: boolean;
  onSubmitForReview?: (reviewerId: number) => Promise<void>;
  onSubmitEumForReview?: (args: EumApproverPickerSubmitArgs) => Promise<void>;
};

export const CaseDetailsFooter = ({
  billingCase,
  onConclusionSubmit,
  onStatusChange,
  isChecker,
  isDraft = false,
  hasLocalChanges = false,
  hasPendingEumChanges = false,
  onSubmitForReview,
  onSubmitEumForReview,
}: CaseDetailsFooterProps): ReactElement | null => {
  const { user } = useAppContext();
  const isGpEumCase = isGpEumRequestType(billingCase.requestType);
  // GP EUM approvers hold GP checker personas (CSM Manager, GP Billing Admin),
  // not the FA checker roles behind `isChecker`; CSM IC is maker-only.
  const isGpChecker = isGpEumCase && !isDraft && hasBillingGPCheckerRole(user?.roles ?? []);
  const showCheckerView = isChecker || isGpChecker;

  if (billingCase.status === "CLOSED") {
    return null;
  }

  if (!showCheckerView && !isDraft) {
    return null;
  }

  return (
    <div className={styles.footerSection} data-testid="case-details-footer">
      {showCheckerView && isGpEumCase && (
        <GpCheckerFooter
          key={billingCase.billingCaseId}
          changeRequestId={billingCase.changeRequestId}
          onConclusionSubmit={onConclusionSubmit}
          onStatusChange={onStatusChange}
        />
      )}
      {showCheckerView && !isGpEumCase && (
        <CheckerFooter
          key={billingCase.billingCaseId}
          changeRequestId={billingCase.changeRequestId}
          assigneeName={billingCase.assignee ?? "Unassigned"}
          assigneeId={billingCase.assigneeId}
          onConclusionSubmit={onConclusionSubmit}
          onStatusChange={onStatusChange}
        />
      )}
      {!showCheckerView && (
        <MakerFooter
          key={billingCase.billingCaseId}
          billingCase={billingCase}
          onStatusChange={onStatusChange}
          isDraft={isDraft}
          hasLocalChanges={hasLocalChanges}
          hasPendingEumChanges={hasPendingEumChanges}
          onSubmitForReview={onSubmitForReview}
          onSubmitEumForReview={onSubmitEumForReview}
        />
      )}
    </div>
  );
};
